import { ReactNode, useEffect, useRef } from "react";
import { TaskStep } from "lib/task-steps";

interface StepProgressOverviewProps {
  steps: TaskStep[];
  unitLabel?: "steps" | "sections";
  renderStepDetails?: (step: TaskStep) => ReactNode;
  currentStepNumber: number;
  isStepCompleted: (stepNumber: number) => boolean;
  isStepStarted: (stepNumber: number) => boolean;
  onSelectStep: (step: TaskStep) => void;
}

export function StepProgressOverview({
  steps,
  unitLabel = "steps",
  renderStepDetails,
  currentStepNumber,
  isStepCompleted,
  isStepStarted,
  onSelectStep,
}: StepProgressOverviewProps) {
  const listRef = useRef<HTMLOListElement>(null);
  const currentItemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const list = listRef.current;
    const currentItem = currentItemRef.current;
    if (!list || !currentItem) return;

    const listBounds = list.getBoundingClientRect();
    const itemBounds = currentItem.getBoundingClientRect();
    if (
      itemBounds.top < listBounds.top ||
      itemBounds.bottom > listBounds.bottom
    ) {
      list.scrollTop +=
        itemBounds.top -
        listBounds.top -
        (list.clientHeight - itemBounds.height) / 2;
    }
  }, [currentStepNumber]);

  if (!steps.length || (steps.length === 1 && unitLabel === "steps"))
    return null;

  const completedSteps = steps.filter((step) =>
    isStepCompleted(step.stepNumber)
  ).length;

  return (
    <details className="rounded-xl border border-f4f7ff bg-white">
      <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-191c20">
        <span className="flex items-center justify-between gap-3">
          <span>
            {steps.length === 1
              ? "View section"
              : `View all ${steps.length} ${unitLabel}`}
          </span>
          <span className="text-858494">{completedSteps} complete</span>
        </span>
      </summary>
      <ol
        ref={listRef}
        className="max-h-64 space-y-2 overflow-y-auto border-t border-f4f7ff px-4 py-3"
      >
        {steps.map((step, index) => {
          const isCurrent = step.stepNumber === currentStepNumber;
          const isComplete = isStepCompleted(step.stepNumber);
          const isStarted = isStepStarted(step.stepNumber);
          const status = isCurrent
            ? "Current"
            : isComplete
            ? "Complete"
            : isStarted
            ? "In progress"
            : "Not started";

          return (
            <li
              key={step.stepNumber}
              ref={isCurrent ? currentItemRef : undefined}
            >
              <button
                type="button"
                disabled={isCurrent}
                aria-current={isCurrent ? "step" : undefined}
                onClick={() => onSelectStep(step)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  isCurrent
                    ? "cursor-default bg-f4f7ff text-191c20"
                    : "cursor-pointer text-858494 hover:bg-f4f7ff hover:text-191c20 focus-visible:bg-f4f7ff focus-visible:text-191c20 active:bg-f4f7ff"
                }`}
              >
                <span
                  className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    isComplete
                      ? "bg-3461fd text-white"
                      : isCurrent
                      ? "border-2 border-3461fd text-3461fd"
                      : "bg-gray-100 text-858494"
                  }`}
                >
                  {isComplete
                    ? "✓"
                    : unitLabel === "sections"
                    ? index + 1
                    : step.stepNumber}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {step.stepLabelDisplayName ||
                    step.stepTitle ||
                    step.stepLabel}
                </span>
                <span className="text-xs">{status}</span>
              </button>
              {renderStepDetails?.(step)}
            </li>
          );
        })}
      </ol>
    </details>
  );
}
